import { OTLayout } from "@/components/layout/ot-layout";
import { Link } from "wouter";
import { TrendingUp, Star, Award, FlaskConical, Users, ChevronRight } from "lucide-react";

const PATHWAYS = [
  {
    href: "/ot/career/band-progression",
    icon: TrendingUp,
    title: "Band Progression",
    subtitle: "Band 5 → 6 → 7 → 8a+",
    description: "NHS Agenda for Change banding, typical role expectations at each band, competency requirements, and tips for successful promotion in rheumatology OT.",
    colour: "amber",
  },
  {
    href: "/ot/career/consultant",
    icon: Star,
    title: "Consultant OT",
    subtitle: "Band 8b–8c pathway",
    description: "The Consultant OT role in rheumatology: development pathway, job planning, advanced scope of practice, national expectations, and building your case for a Consultant post.",
    colour: "orange",
  },
  {
    href: "/ot/career/frcot",
    icon: Award,
    title: "RCOT Fellowship (FRCOT)",
    subtitle: "Pathway to FRCOT",
    description: "Eligibility criteria, application process, fellowship categories, and practical guidance for rheumatology OTs aspiring to RCOT Fellowship (FRCOT).",
    colour: "rose",
  },
  {
    href: "/ot/career/academic",
    icon: FlaskConical,
    title: "Academic OT",
    subtitle: "Clinical academic & research routes",
    description: "Academic career paths for rheumatology OTs: NIHR internships, PhD routes, clinical academic appointments, RCOT research grants, and balancing clinical and academic work.",
    colour: "violet",
  },
  {
    href: "/ot/career/ss-rheumatology",
    icon: Users,
    title: "SS Rheumatology Specialist Career",
    subtitle: "RCOT Specialist Section pathway",
    description: "Building a specialist career through RCOT SS Rheumatology: committee involvement, national influence, specialist competency recognition, and contributing to the profession.",
    colour: "blue",
  },
];

const COLOUR_MAP: Record<string, { bg: string; border: string; icon: string; text: string; hover: string }> = {
  amber: { bg: "bg-amber-50", border: "border-amber-200", icon: "text-amber-600", text: "text-amber-800", hover: "hover:border-amber-300" },
  orange: { bg: "bg-orange-50", border: "border-orange-200", icon: "text-orange-600", text: "text-orange-800", hover: "hover:border-orange-300" },
  rose: { bg: "bg-rose-50", border: "border-rose-200", icon: "text-rose-600", text: "text-rose-800", hover: "hover:border-rose-300" },
  violet: { bg: "bg-violet-50", border: "border-violet-200", icon: "text-violet-600", text: "text-violet-800", hover: "hover:border-violet-300" },
  blue: { bg: "bg-blue-50", border: "border-blue-200", icon: "text-blue-600", text: "text-blue-800", hover: "hover:border-blue-300" },
};

export default function OtCareerHub() {
  return (
    <OTLayout title="Career Pathways">
      <div className="space-y-6">

        <div className="bg-gradient-to-br from-amber-700 to-orange-800 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="h-7 w-7 text-amber-300" />
            <div>
              <h2 className="text-xl font-bold">OT Career Pathways in Rheumatology</h2>
              <p className="text-amber-200 text-sm">Band 5 to Consultant OT, FRCOT, and beyond</p>
            </div>
          </div>
          <p className="text-amber-100 text-sm leading-relaxed">
            Explore structured career pathways for rheumatology occupational therapists — from your first post as a Band 5 OT through to Consultant OT, RCOT Fellowship, and academic or specialist roles. Use the RCOT Career Development Framework and your CPD portfolio to chart your journey.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {PATHWAYS.map((p) => {
            const c = COLOUR_MAP[p.colour];
            return (
              <Link key={p.href} href={p.href}>
                <div className={`bg-white rounded-2xl border p-5 cursor-pointer transition-all shadow-sm hover:shadow-md group ${c.border} ${c.hover}`}>
                  <div className="flex items-start gap-4">
                    <div className={`p-2.5 rounded-xl ${c.bg} shrink-0`}>
                      <p.icon className={`h-6 w-6 ${c.icon}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-bold text-slate-900 group-hover:text-amber-800 transition-colors">{p.title}</h3>
                        <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-amber-500 shrink-0 transition-colors" />
                      </div>
                      <p className={`text-xs font-medium ${c.text} mb-1`}>{p.subtitle}</p>
                      <p className="text-sm text-slate-500 leading-relaxed">{p.description}</p>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-3">Useful Starting Points</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Link href="/ot/cpd/advanced-practice">
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors">
                <p className="text-sm font-semibold text-amber-800">Advanced Practice Portfolio</p>
                <p className="text-xs text-slate-500 mt-1">Map evidence to RCOT Career Development Framework stages</p>
              </div>
            </Link>
            <Link href="/ot/cpd/revalidation">
              <div className="p-4 bg-orange-50 rounded-xl border border-orange-200 cursor-pointer hover:bg-orange-100 transition-colors">
                <p className="text-sm font-semibold text-orange-800">HCPC Revalidation</p>
                <p className="text-xs text-slate-500 mt-1">Track your renewal date and audit readiness</p>
              </div>
            </Link>
            <Link href="/ot/cpd/reflection">
              <div className="p-4 bg-rose-50 rounded-xl border border-rose-200 cursor-pointer hover:bg-rose-100 transition-colors">
                <p className="text-sm font-semibold text-rose-800">Reflection Tool</p>
                <p className="text-xs text-slate-500 mt-1">Structured reflection using Kolb or Johns' model</p>
              </div>
            </Link>
          </div>
        </div>

      </div>
    </OTLayout>
  );
}
